import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ModalPortal from './ModalPortal';

export interface NotificationToastProps {
  notification: {
    id: number;
    type: string;
    actor?: {
      id?: number;
      name?: string;
      username?: string;
      image?: string;
    };
    post?: {
      caption?: string;
    };
    comment?: {
      content?: string;
    };
    createdAt: string;
  };
  onClose: () => void;
  onClick?: () => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onClose,
  onClick,
}) => {
  // Auto-close after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const getNotificationText = () => {
    const actorName = notification.actor?.name || notification.actor?.username || 'کاربر';
    
    switch (notification.type) {
      case 'post_liked':
        return `${actorName} پست شما را لایک کرد`;
      case 'comment':
        return `${actorName} روی پست شما کامنت گذاشت`;
      case 'follow':
        return `${actorName} شما را دنبال کرد`;
      default:
        return 'اعلان جدید';
    }
  };

  return (
    <ModalPortal>
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 300, scale: 0.9 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 300, scale: 0.9 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-4 left-4 z-[9999] max-w-sm w-full"
        onClick={onClick}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 flex items-start gap-3 cursor-pointer hover:shadow-2xl transition-shadow">
          {/* Icon */}
          <div className="flex-shrink-0">
            {notification.actor?.image ? (
              <img
                src={notification.actor.image}
                alt={notification.actor.name || notification.actor.username}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <NotificationsIcon className="w-6 h-6 text-white" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {getNotificationText()}
            </p>
            {notification.post?.caption && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                {notification.post.caption}
              </p>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="بستن"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
    </ModalPortal>
  );
};

export default NotificationToast;
